import React, { useState, useEffect } from "react";
import BrandLogo from "../common/BrandLogo";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const B = "#E1E9EE";

/**
 * Top navigation bar — responsive with mobile hamburger menu.
 */
export default function NavBar({ page, onPageChange, user, pendingCount = 0, onSettingsTab }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const isTeamMember = user?.is_workspace_member || user?.is_workspace_admin;
  const isTeamAdmin = user?.is_workspace_admin;
  let tabs = [
    ["dashboard", "Home"],
    ["posts", "Posts"],
    ["authority-presence", "My Plug"],
    ["team", "Teams"],
    ["settings", "Settings"],
  ];
  // Hide Teams tab for workspace members who aren't admins
  if (isTeamMember && !isTeamAdmin) {
    tabs = tabs.filter(([id]) => id !== "team");
  }
  // Hide My Plug for all workspace users (admins manage via Teams, members via workspace dashboard)
  if (isTeamMember) {
    tabs = tabs.filter(([id]) => id !== "plug");
  }

  const handleTabClick = (p) => {
    onPageChange(p);
    setShowMobileMenu(false);
  };

  const handleSignOut = () => {
    localStorage.removeItem("nectar_st");
    fetch(`${API_BASE}/app/api/auth/logout`, { method: "POST", credentials: "include" }).finally(() => {
      window.location.href = "/";
    });
  };

  return (
    <div style={{ borderBottom: `1px solid ${B}`, padding: isMobile ? "0 16px" : "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 52, position: "sticky", top: 0, background: "#FFFFFF", zIndex: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        <BrandLogo size="md" showText={false} />
      </div>

      {/* Desktop tabs */}
      {!isMobile && (
        <div style={{ display: "flex", gap: 2 }}>
          {tabs.map(([p, label]) => {
            const isTeamsUpsell = p === "team" && !isTeamMember;
            return (
              <button key={p} onClick={() => handleTabClick(p)} style={{ background: "transparent", color: page === p ? "#0077B5" : isTeamsUpsell ? "#AAAAAA" : "#666666", border: "none", borderBottom: page === p ? "2px solid #0077B5" : "2px solid transparent", borderRadius: 0, padding: "0 14px", height: 52, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, fontWeight: page === p ? 600 : 400, cursor: "pointer", transition: "all 0.15s", position: "relative" }}>
                {label}
                {isTeamsUpsell && <span style={{ marginLeft: 4, fontSize: 10, background: "#F5A623", color: "#fff", borderRadius: 4, padding: "1px 5px", fontWeight: 600, verticalAlign: "middle" }}>NEW</span>}
                {p === "posts" && pendingCount > 0 && (
                  <span style={{ position: "absolute", top: 10, right: 6, width: 7, height: 7, background: G, borderRadius: "50%" }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Right side: hamburger (mobile) + user avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {isMobile && (
          <button
            onClick={() => setShowMobileMenu(m => !m)}
            aria-label="Toggle menu"
            style={{ background: "transparent", border: "none", padding: 8, cursor: "pointer", lineHeight: 1 }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 24, height: 24, color: "#1a1a1a" }}>
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
        )}

        {!isMobile && (
          <div style={{ fontSize: 15, color: "#666666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            {(user?.linkedin_name || user?.first_name || "").split(" ")[0]}
          </div>
        )}

        <div onClick={() => setShowMenu(m => !m)} style={{ cursor: "pointer", position: "relative" }}>
          {user?.linkedin_id && !avatarError ? (
            <img
              src={`${API_BASE}/app/api/user/avatar?user_id=${user.id}`}
              alt={user?.first_name || "You"}
              style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#0077B5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 16, color: "#fff", fontWeight: 600 }}>{(user?.linkedin_name || user?.first_name || "?")[0].toUpperCase()}</span>
            </div>
          )}
        </div>

        {/* User dropdown menu */}
        {showMenu && (
          <div
            style={{ position: "absolute", top: 52, right: 16, background: "#fff", border: `1px solid ${B}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 200, minWidth: 180, overflow: "hidden" }}
            onMouseLeave={() => setShowMenu(false)}
          >
            <button
              onClick={() => { setShowMenu(false); onPageChange("settings"); if (onSettingsTab) onSettingsTab("profile"); }}
              style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "14px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#333", cursor: "pointer", minHeight: 48 }}
            >Profile Settings</button>
            <div style={{ height: 1, background: B }} />
            <button
              onClick={handleSignOut}
              style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "14px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#dc3545", cursor: "pointer", minHeight: 48 }}
            >Sign out</button>
          </div>
        )}
      </div>

      {/* Mobile slide-down menu */}
      {isMobile && showMobileMenu && (
        <>
          <div
            onClick={() => setShowMobileMenu(false)}
            style={{ position: "fixed", inset: 0, top: 52, background: "rgba(0,0,0,0.3)", zIndex: 99 }}
          />
          <div style={{ position: "absolute", top: 52, left: 0, right: 0, background: "#fff", borderBottom: `1px solid ${B}`, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100 }}>
            {tabs.map(([p, label]) => {
              const isTeamsUpsell = p === "team" && !isTeamMember;
              return (
                <button
                  key={p}
                  onClick={() => handleTabClick(p)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: page === p ? "#EBF5FB" : "transparent",
                    color: page === p ? "#0077B5" : isTeamsUpsell ? "#AAAAAA" : "#333",
                    border: "none",
                    borderBottom: `1px solid ${B}`,
                    padding: "16px 24px",
                    fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
                    fontSize: 16,
                    fontWeight: page === p ? 600 : 400,
                    cursor: "pointer",
                    minHeight: 56,
                    position: "relative",
                  }}
                >
                  {label}
                  {isTeamsUpsell && <span style={{ marginLeft: 6, fontSize: 10, background: "#F5A623", color: "#fff", borderRadius: 4, padding: "2px 6px", fontWeight: 600, verticalAlign: "middle" }}>NEW</span>}
                  {p === "posts" && pendingCount > 0 && (
                    <span style={{ position: "absolute", top: "50%", right: 24, transform: "translateY(-50%)", width: 8, height: 8, background: G, borderRadius: "50%" }} />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
